import React from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Link } from 'react-router-dom';
import { makeStyles } from '@lifeomic/chroma-react/styles';

import urls from '../../../../util/urls';
import {
  lifecycleStateMessages,
  provenanceStatusMessages,
  regenerativeMedicineOpsMessages,
} from '../messages';
import type { RegenerativeMedicineMemberDetail } from '../types';
import { LIFECYCLE_STEP_ORDER } from '../types';
import { LifecyclePill } from './LifecyclePill';
import { StatusBadge, type StatusBadgeVariant } from './StatusBadge';

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.pxToRem(4),
    boxShadow: theme.boxShadows.table,
    padding: theme.spacing(2),
  },
  evidenceCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    padding: theme.spacing(1.5),
  },
  stepLabel: {
    flex: 1,
    minWidth: 0,
    paddingRight: theme.spacing(1),
  },
  stepBadge: {
    flexShrink: 0,
  },
}));

type MemberDetailPanelProps = {
  detail: RegenerativeMedicineMemberDetail;
  showMemberLink?: boolean;
};

const provenanceVariant = (
  status:
    | RegenerativeMedicineMemberDetail['lifecycleProvenance'][string]['status']
    | undefined,
): StatusBadgeVariant => {
  switch (status) {
    case 'synced':
      return 'synced';
    case 'missing':
      return 'missing';
    case 'not_available':
      return 'unavailable';
    default:
      return 'missing';
  }
};

export const MemberDetailPanel: React.FC<MemberDetailPanelProps> = ({
  detail,
  showMemberLink = true,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  return (
    <Box direction="column" gap={3}>
      <Box className={classes.card} direction="column" gap={1}>
        <Text size="headline" weight="bold">
          {detail.displayName}
        </Text>
        <LifecyclePill state={detail.lifecycleState} />
        {showMemberLink ? (
          <Link
            to={`${urls.app.members(
              detail.patientId,
            )}/regenerative-medicine-ops`}
          >
            {formatMessage(regenerativeMedicineOpsMessages.openMemberDashboard)}
          </Link>
        ) : null}
      </Box>

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(regenerativeMedicineOpsMessages.provenanceTitle)}
        </Text>
        <Box direction="column" gap={1}>
          {LIFECYCLE_STEP_ORDER.map(step => {
            const prov = detail.lifecycleProvenance[step];
            return (
              <Box
                key={step}
                direction="row"
                gap={1}
                align="center"
                justify="space-between"
                className={classes.evidenceCard}
              >
                <Text size="subbody" className={classes.stepLabel}>
                  {formatMessage(lifecycleStateMessages[step])}
                </Text>
                <span className={classes.stepBadge}>
                  <StatusBadge
                    variant={provenanceVariant(prov?.status)}
                    label={formatMessage(
                      provenanceStatusMessages[prov?.status ?? 'missing'],
                    )}
                  />
                </span>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(regenerativeMedicineOpsMessages.evidenceTitle)}
        </Text>
        {detail.evidence.length === 0 ? (
          <Text size="subbody">
            {formatMessage(regenerativeMedicineOpsMessages.emptyEvidence)}
          </Text>
        ) : (
          detail.evidence.map(event => (
            <Box
              key={`${event.type}-${event.occurredAt ?? event.summary}`}
              className={classes.evidenceCard}
              direction="column"
              gap={0.5}
            >
              <Text weight="bold">{event.summary}</Text>
              <Text size="caption">
                {event.provenance.sourceSystem} ·{' '}
                {formatMessage(
                  provenanceStatusMessages[event.provenance.status],
                )}
                {event.occurredAt ? ` · ${event.occurredAt}` : ''}
              </Text>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};
